import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  AuthContext,
  Icon,
  Loader,
  useAPI,
  useNavigate
} from "@/components/lib";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Ximage from "@/images/prime_twitter.svg";
import HudlImage from "@/images/hudl_logo.svg";
import collegeOffers from "@/images/college_offers.png";
import defaultPlayerImage from "@/images/default_american_football.png";
import Styles from "@/css/mainStyles";
import ShareModal from "./modals/share";
import FullGalleryModal from "./modals/fullGallery";

export default function PlayerInfo() {
  const authContext = useContext<any>(AuthContext);
  const { user } = authContext;
  const { playerName } = useParams();
  const navigate = useNavigate();
  const { data: player, loading } = useAPI(
    `/api/player/profile?profileName=${playerName}`
  );
  const { data: coaches } = useAPI(`/api/coach`);
  const [galleryModal, setGalleryModal] = useState({ view: false, index: 0 });
  const [shareModal, setShareModal] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);
  const filesURL = "https://ghs-general.s3.us-east-1.amazonaws.com/";
  const profile_image = filesURL + player?.profile_pic;

  const gallery_images =
    player?.gallery?.length > 0
      ? player.gallery.map((file: any) => ({
          type: file.format,
          url: filesURL + file.url,
          thumbnail: file.thumbnail && filesURL + file.thumbnail
        }))
      : [];

  const coach =
    player?.info?.coach_id && coaches?.length > 0
      ? coaches?.find((coach: any) => coach.id === player.info.coach_id)
      : null;

  const playerOffers = player?.info?.offers
    ? player.info.offers.split(/[,;]/)
    : [];

  useEffect(() => {
    if (galleryModal.view) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [galleryModal]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (loading)
    return (
      <div className="w-full flex justify-center pt-10">
        <Loader />
      </div>
    );

  const generateTranscript = () => {
    const url = `mailto:${player.email}?subject=${encodeURI(
      "Transcript Request"
    )}`;
    window.location.href = url;
  };

  const openImage = (index) => {
    setGalleryModal({ view: true, index });
  };

  const handleShare = () => {
    setShareModal(true);
  };

  const VideoComponent = ({ image, index, openImage }) => {
    const [error, setError] = useState(false);

    return (
      <div className="relative">
        {error ? (
          <div className="w-full h-36 flex items-center justify-center">
            <Icon image="video-off" color="white" size={40} />
          </div>
        ) : (
            <video
              src={image.url}
              poster={image.thumbnail || "https://ghs-general.s3.us-east-1.amazonaws.com/thumnail.png"}
              className="w-full h-36 cursor-pointer"
              onClick={() => openImage(index)}
              onError={() => setError(true)}
              preload="auto"
              typeof="video/mp4"
            />
        )}
      </div>
    );
  };

  function formatToDecimal(value, index) {
    let number = parseFloat(value);
    if (isNaN(number)) {
      return "0.0";
    }
    return number.toFixed(index);
  }

  if (player && !player?.hidden)
    return (
      <div className="pb-10 md:flex gap-4">
        <Helmet>
          <title>
            {player.first_name + " " + player.last_name + " #" + player.shirt}
          </title>
          <meta
            property="og:title"
            content={
              player.first_name + " " + player.last_name + " #" + player.shirt
            }
          />
          <meta
            property="og:image"
            content={player?.profile_pic ? profile_image : defaultPlayerImage}
          />
        </Helmet>
        <div className="md:w-[48%]">
          <div className="relative w-full overflow-hidden shadow-lg min-h-[300px]">
            {!imageError && player?.profile_pic ? (
              <img
                src={profile_image}
                alt="Grapevine High School"
                className="w-full h-[450px] md:h-[550px] lg:h-[600px] object-cover overflow-hidden"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={defaultPlayerImage}
                  alt="Grapevine High School"
                  className="w-full lg:w-3/5 h-[300px] md:h-[400px] lg:h-full"
                />
              </div>
            )}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(7, 16, 27, 0) 50%, #07101B 100%)"
              }}
            />
            <a
              href="/players"
              className="rounded-full bg-white p-2 absolute top-8 left-6"
            >
              <Icon
                size={20}
                image="arrow-left"
                className="text-blue-600 w-6 h-6"
              />
            </a>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 px-4 pb-2 text-white w-full text-left">
              <div className="flex flex-col">
                <h3 className="text-3xl font-bold">
                  {player.first_name} {player.last_name} #{player.shirt}
                </h3>
                <div className="flex justify-between items-end">
                  <div className="flex gap-2 items-center justify-start w-full mt-1">
                    {player.info?.height?.feet ? (
                      <p className="text-lg bg-[#FFFFFF1A] rounded-full px-2 py-1 w-fit">
                        {player.info.height.feet}'
                        {player.info?.height?.inches || 0}"
                      </p>
                    ) : null}
                    {player.info?.weight ? (
                      <p className="text-lg bg-[#FFFFFF1A] rounded-full px-2 py-1 w-fit">
                        {player.info.weight} lbs
                      </p>
                    ) : null}
                    <p className="text-lg bg-[#FFFFFF1A] rounded-full px-2 py-1 w-fit">
                      {player.info?.position || "None"}
                    </p>
                    <p className="text-lg bg-[#FFFFFF1A] rounded-full px-2 py-1 w-fit">
                      {player.info?.class || "None"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 items-end mb-2">
                  {player.info?.offers && (
                    <img
                      src={collegeOffers}
                      alt="offers"
                      className="w-48 h-9"
                    />
                  )}
                  <div className="flex gap-2 items-end ml-auto">
                    {player?.hudl && (
                      <a
                        href={`https://www.hudl.com/profile/` + player.hudl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={HudlImage} alt="X" className="w-18" />
                      </a>
                    )}
                    {player?.twitter_id && (
                      <a
                        href={`https://x.com/` + player.twitter_id}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={Ximage} alt="X" className="w-18" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:grid grid-cols-4 gap-2 px-2 hidden mt-4">
            {gallery_images?.map(
              (image: { url: string; type: string }, index: number) => {
                if (image.type === "image")
                  return (
                    <img
                      key={index}
                      src={image.url}
                      alt="Grapevine High School"
                      className="w-full h-36 cursor-pointer"
                      onClick={() => openImage(index)}
                    />
                  );
                if (image.type === "video")
                  return (
                    <VideoComponent
                      key={index}
                      image={image}
                      index={index}
                      openImage={openImage}
                    />
                  );
                return null;
              }
            )}
          </div>
        </div>
        <div className="space-y-2 font-bold px-4 md:w-[52%] md:text-lg">
          <div className="flex flex-col lg:flex-row justify-between items-center flex-wrap">
            <h1 className="hidden md:block text-3xl lg:text-5xl text-white">
              {player.first_name} {player.last_name} #{player.shirt}
            </h1>
            {user?.profileName === playerName ? (
              <div className="flex gap-4 my-4 px-4 w-full md:w-auto md:ml-auto">
                <a
                  href={`/player/edit`}
                  className={Styles.button + " text-center"}
                >
                  Edit Profile
                </a>
                <a
                  href={`/player/settings`}
                  className={Styles.button + " text-center"}
                >
                  Account Settings
                </a>
              </div>
            ) : (
              <button
                type="button"
                className={Styles.button + " mt-4 md:mt-0"}
                onClick={handleShare}
              >
                Share
              </button>
            )}
          </div>
          <h2 className="text-white">Strength & Speed</h2>
          <div className="space-y-1">
            <div className="flex gap-2 items-center">
              <p>Pro Agility:</p>
              <p className="text-white">
                {player.info?.agility && player.info?.agility !== 0
                  ? player.info.agility
                  : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>40 Time:</p>
              <p className="text-white">
                {player.info?.fourty && player.info.fourty !== 0
                  ? player.info.fourty
                  : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>Broad Jump:</p>
              <p className="text-white">
                {player.info?.jump?.feet ? player.info.jump.feet + "'" : ""}
                {player.info?.jump?.inches ? player.info.jump.inches + '"' : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>Vertical:</p>
              <p className="text-white">
                {player.info?.vertical?.feet
                  ? player.info.vertical.feet + '"'
                  : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>Bench Max:</p>
              <p className="text-white">
                {player.info?.bench && player.info.bench !== 0
                  ? player.info.bench
                  : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>Squat Max:</p>
              <p className="text-white">
                {player.info?.squat && player.info.squat !== 0
                  ? player.info.squat
                  : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>Clean Max:</p>
              <p className="text-white">
                {player.info?.clean && player.info.clean !== 0
                  ? player.info.clean
                  : ""}
              </p>
            </div>
          </div>
          <h2 className="text-white">Academics</h2>
          <div className="space-y-1">
            <div className="flex gap-2 items-center">
              <p>GPA:</p>
              <p className="text-white">
                {player.info?.gpa && player.info.gpa !== 0
                  ? formatToDecimal(player.info.gpa, 1)
                  : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>SAT:</p>
              <p className="text-white">
                {player.info?.sat && player.info.sat !== 0
                  ? player.info.sat
                  : ""}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <p>ACT:</p>
              <p className="text-white">
                {player.info?.act && player.info.act !== 0
                  ? player.info.act
                  : ""}
              </p>
            </div>
          </div>
          {player?.email && user?.profileName !== playerName && (
            <button
              type="button"
              onClick={generateTranscript}
              className={Styles.button + " mx-auto md:ml-auto md:mr-0"}
            >
              Request Transcript
            </button>
          )}
          {gallery_images?.length > 0 && (
            <h2 className="text-white md:hidden">Gallery</h2>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:hidden">
            {gallery_images?.map(
              (image: { url: string; type: string }, index: number) => {
                if (image.type === "image")
                  return (
                    <img
                      key={index}
                      src={image.url}
                      alt="Grapevine High School"
                      className="w-full h-36 cursor-pointer"
                      onClick={() => openImage(index)}
                    />
                  );
                if (image.type === "video")
                  return (
                    <VideoComponent
                      key={index}
                      image={image}
                      index={index}
                      openImage={openImage}
                    />
                  );
                return null;
              }
            )}
          </div>
          {player.info?.recognition && (
            <>
              <h2 className="text-white">Stats & Recognition</h2>
              <div>
                <p
                  className="text-white font-thin"
                  dangerouslySetInnerHTML={{
                    __html: player.info.recognition.replace(/\n/g, "<br />")
                  }}
                />
              </div>
            </>
          )}
          {playerOffers.length > 0 && (
            <>
              <h2 className="text-white">College Offers</h2>
              <div className="flex gap-2">
                {playerOffers.map((offer: any, index: number) => (
                  <p
                    key={index}
                    className="text-white text-base rounded-full py-2 px-4 bg-[#FFFFFF1A]"
                  >
                    {offer}
                  </p>
                ))}
              </div>
            </>
          )}
          <h2 className="text-white">Contact</h2>
          <div className="space-y-1">
            <div className="flex gap-2 items-center">
              <p>Email</p>
              <a href={"mailto:" + player.email} className="text-white">
                {player.email}
              </a>
            </div>
            <div className="flex gap-2 items-center">
              <p>High School</p>
              <p className="text-white">{player.info?.school}</p>
            </div>
            <div className="flex gap-2 items-center">
              <p>City & State</p>
              <p className="text-white">
                {(player.info?.city ? player.info.city + ", " : "") +
                  (player.info?.state || "")}
              </p>
            </div>
          </div>
          {coach && (
            <>
              <div className="flex gap-2 items-center my-2">
                <p>Coach</p>
                <p className="text-white">
                  {coach.first_name + " " + coach.last_name}
                </p>
              </div>
              <div className="flex gap-2 items-center">
                <p>Coach Email</p>
                <a href={"mailto:" + coach.email} className="text-white">
                  {coach.email}
                </a>
              </div>
            </>
          )}
        </div>
        {galleryModal.view && (
          <FullGalleryModal
            setOpen={setGalleryModal}
            items={gallery_images}
            startIndex={galleryModal.index}
          />
        )}

        {shareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center md:justify-center">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 100 }}
              className="bg-white w-full p-6 rounded-t-2xl shadow-lg md:hidden"
            >
              <ShareModal
                playerName={playerName}
                twitter_id={player?.twitter_id}
                hudl={player?.hudl}
                setOpen={setShareModal}
              />
            </motion.div>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 100 }}
              className="hidden md:block bg-white w-full md:w-1/3 p-6 rounded-2xl shadow-lg"
            >
              <ShareModal
                playerName={playerName}
                twitter_id={player?.twitter_id}
                hudl={player?.hudl}
                setOpen={setShareModal}
              />
            </motion.div>
          </div>
        )}
      </div>
    );

  return (
    <button
      type="button"
      onClick={() => navigate("/players")}
      className={Styles.button + " mx-auto"}
    >
      Go to Players
    </button>
  );
}
